// Der Naht zur API: gleiche Herkunft, relative Pfade — so löst der Host-Header den Mandanten
// auf (kein CORS). Alle Geldbeträge sind ganze Cent. Die Typen spiegeln exakt die
// Außenansichten der NestJS-Dienste (siehe apps/api).
import { euro } from "./geld";

export type Kanal = "LIEFERUNG" | "ABHOLUNG";

export type Bestellstatus = "NEU" | "ANGENOMMEN" | "ABGELEHNT" | "STORNIERT" | "ERLEDIGT";

/** Eine Zutat, wie sie die Speisekarte zeigt — mit Verfügbarkeits-Markierung. */
export interface ZutatAnsicht {
  name: string;
  ausverkauft: boolean;
}

/** Eine Speise der öffentlichen Speisekarte (GET /katalog/speisen). */
export interface SpeiseAnsicht {
  id: string;
  name: string;
  stueckpreisCent: number;
  bestellbar: boolean;
  allergene: string[];
  zutaten: ZutatAnsicht[];
}

/** Strukturierte Lieferadresse (ADR-0007). */
export interface Lieferadresse {
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
}

/** Eine Warenkorb-Zeile für das OTP: eine Speise plus kostenlose Weglassungen. */
export interface BestellzeileEingabe {
  speiseId: string;
  weglassen?: string[];
}

/** Eingabe für POST /bestellungen/otp. */
export interface OtpAnfordernEingabe {
  telefon: string;
  kanal: Kanal;
  lieferadresse?: Lieferadresse;
  zeilen: BestellzeileEingabe[];
}

/** Eine eingefrorene Zeile, wie Review und Bestellung sie zeigen. */
export interface ZeileAnsicht {
  name: string;
  stueckpreisCent: number;
  zeilensummeCent: number;
  allergene: string[];
}

/** Der Review vor dem OTP (POST /bestellungen/otp). `code` nur im Dev-Modus. */
export interface ReviewAnsicht {
  challengeId: string;
  zeilen: ZeileAnsicht[];
  liefergebuehrCent: number;
  gesamtsummeCent: number;
  /** Vom Dev-Bypass mitgeliefert (OTP_DEV_MODE); dann vorausgefüllt. */
  code?: string;
}

/** Die bestätigte Bestellung (POST /bestellungen). */
export interface BestellungAnsicht {
  id: string;
  status: Bestellstatus;
  kanal: Kanal;
  liefergebuehrCent: number;
  gesamtsummeCent: number;
  ablehnungsgrund: string | null;
  wartezeitMin: number | null;
  zeilen: ZeileAnsicht[];
}

/** Ein Eintrag der Koch-Live-Liste (GET /bestellungen). */
export interface KochBestellungAnsicht extends BestellungAnsicht {
  erstelltAm: string;
  telefon: string;
  lieferadresse: Lieferadresse | null;
}

/** Stand eines Verzögerungs-Hebels (POST/DELETE /betrieb/verzoegerung/:kanal). */
export interface VerzoegerungAnsicht {
  kanal: Kanal;
  verzoegerungMin: number;
}

/** Der Fehlerkörper, wie NestJS ihn liefert — plus die Vor-Tor-Felder der Lieferung. */
interface FehlerKoerper {
  statusCode?: number;
  message?: string | string[];
  error?: string;
  grund?: string;
  mindestbestellwertCent?: number;
}

/** Ein API-Fehler mit HTTP-Status und (falls vorhanden) strukturiertem Körper. */
export class ApiFehler extends Error {
  readonly status: number;
  readonly koerper: FehlerKoerper | undefined;

  constructor(status: number, koerper: FehlerKoerper | undefined) {
    super(leseNachricht(status, koerper));
    this.name = "ApiFehler";
    this.status = status;
    this.koerper = koerper;
  }
}

/** Leitet eine lesbare deutsche Meldung aus Status und Körper ab. */
function leseNachricht(status: number, koerper: FehlerKoerper | undefined): string {
  // Vor-Tor Lieferung (422): außerhalb Gebiet oder unter Mindestbestellwert.
  if (status === 422 && koerper) {
    if (koerper.grund === "ausserhalb_liefergebiet") {
      return koerper.message ? String(koerper.message) : "Zu dieser PLZ liefern wir leider nicht.";
    }
    if (typeof koerper.mindestbestellwertCent === "number") {
      return `Mindestbestellwert nicht erreicht: ${euro(koerper.mindestbestellwertCent)}.`;
    }
  }

  const ausKoerper = koerper?.message;
  if (Array.isArray(ausKoerper) && ausKoerper.length > 0) return ausKoerper.join(" ");
  if (typeof ausKoerper === "string" && ausKoerper.trim()) return ausKoerper;

  switch (status) {
    case 400:
      return "Ungültige Eingabe.";
    case 401:
      return "Nicht autorisiert — bitte das Dashboard-Token prüfen.";
    case 404:
      return "Nicht gefunden.";
    case 409:
      return "Konflikt — der Zustand hat sich geändert.";
    case 410:
      return "Die OTP-Anforderung ist abgelaufen oder bereits eingelöst.";
    default:
      return `Unerwarteter Fehler (HTTP ${status}).`;
  }
}

/** Der gemeinsame fetch-Kern: relative URL, JSON, Fehler als ApiFehler. */
async function anfrage<T>(pfad: string, optionen: RequestInit = {}): Promise<T> {
  const antwort = await fetch(pfad, {
    ...optionen,
    headers: {
      Accept: "application/json",
      ...(optionen.body ? { "Content-Type": "application/json" } : {}),
      ...optionen.headers,
    },
  });

  if (!antwort.ok) {
    let koerper: FehlerKoerper | undefined;
    try {
      koerper = (await antwort.json()) as FehlerKoerper;
    } catch {
      koerper = undefined;
    }
    throw new ApiFehler(antwort.status, koerper);
  }

  if (antwort.status === 204) return undefined as T;
  return (await antwort.json()) as T;
}

/** Baut die Authorization-Header aus dem Dashboard-Token (Bearer). */
function authHeader(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

// --- Storefront (offen) -------------------------------------------------------

export function speisekarte(): Promise<SpeiseAnsicht[]> {
  return anfrage<SpeiseAnsicht[]>("/katalog/speisen");
}

export function otpAnfordern(eingabe: OtpAnfordernEingabe): Promise<ReviewAnsicht> {
  return anfrage<ReviewAnsicht>("/bestellungen/otp", {
    method: "POST",
    body: JSON.stringify(eingabe),
  });
}

export function bestellungBestaetigen(challengeId: string, code: string): Promise<BestellungAnsicht> {
  return anfrage<BestellungAnsicht>("/bestellungen", {
    method: "POST",
    body: JSON.stringify({ challengeId, code }),
  });
}

// --- Dashboard (Bearer-Token) -------------------------------------------------

export function aktiveBestellungen(token: string): Promise<KochBestellungAnsicht[]> {
  return anfrage<KochBestellungAnsicht[]>("/bestellungen", { headers: authHeader(token) });
}

export function bestellungAnnehmen(token: string, id: string): Promise<BestellungAnsicht> {
  return anfrage<BestellungAnsicht>(`/bestellungen/${id}/annehmen`, {
    method: "POST",
    headers: authHeader(token),
  });
}

export function bestellungAblehnen(
  token: string,
  id: string,
  grund: string,
): Promise<BestellungAnsicht> {
  return anfrage<BestellungAnsicht>(`/bestellungen/${id}/ablehnen`, {
    method: "POST",
    headers: authHeader(token),
    body: JSON.stringify({ grund }),
  });
}

export function bestellungErledigen(token: string, id: string): Promise<BestellungAnsicht> {
  return anfrage<BestellungAnsicht>(`/bestellungen/${id}/erledigen`, {
    method: "POST",
    headers: authHeader(token),
  });
}

export function verzoegerungErhoehen(
  token: string,
  kanal: Kanal,
  schrittMin: number,
): Promise<VerzoegerungAnsicht> {
  return anfrage<VerzoegerungAnsicht>(`/betrieb/verzoegerung/${kanalPfad(kanal)}`, {
    method: "POST",
    headers: authHeader(token),
    body: JSON.stringify({ schrittMin }),
  });
}

export function verzoegerungZuruecksetzen(
  token: string,
  kanal: Kanal,
): Promise<VerzoegerungAnsicht> {
  return anfrage<VerzoegerungAnsicht>(`/betrieb/verzoegerung/${kanalPfad(kanal)}`, {
    method: "DELETE",
    headers: authHeader(token),
  });
}

/** Übersetzt den Enum-Kanal in das Pfad-Segment („lieferung"/„abholung"). */
function kanalPfad(kanal: Kanal): "lieferung" | "abholung" {
  return kanal === "LIEFERUNG" ? "lieferung" : "abholung";
}
